import { useState } from 'react'
import { Loader2, User, Users } from 'lucide-react'
import MeetingPulseBar from './live-controls/MeetingPulseBar'
import LateStartBand from './live-controls/LateStartBand'
import ActionRequiredBand from './live-controls/ActionRequiredBand'
import SharedContextColumn from './live-controls/SharedContextColumn'
import PersonalContextColumn from './live-controls/PersonalContextColumn'
import ToneFogOverlay from './live-controls/ToneFogOverlay'

// Live negativity that drives the border fog: the viewer's OWN tone only.
// The fog is a personal signal ("your tone is trending negative"), so it must
// never fire on someone else's negativity. Blending in the room's overall tone
// here made the fog light up while the viewer was silent and another
// participant was heated — it could not tell speakers apart. The per-speaker
// room sentiment is still surfaced separately via overallToneSignal in the
// personal column; it just doesn't drive the viewer's personal fog.
function negativityIntensityFromInsights(insights) {
  const pv = insights?.personalView
  if (!pv) return 0
  return pv.viewerToneSignal?.negativityIntensity ?? 0
}

// ── Mobile/Tablet Tab Switcher for Zone 3 ──────────────────────

function MobileTabView({
  sharedTeamView,
  personalView,
  companionView = null,
  recapOutputs = [],
  memberPrivateGoalsState = null,
  meetingId = null,
  audienceMode = 'internal',
  pulseBarVisible = false,
  onPersonalAction,
  readOnly = false,
}) {
  const [mobileTab, setMobileTab] = useState('shared')

  return (
    <div className="lg:hidden flex flex-col gap-3">
      {/* Tab switcher */}
      <div role="tablist" className="flex rounded-xl border border-gray-200 bg-gray-50 p-1 gap-1">
        {[
          { id: 'shared', label: 'Shared', icon: Users },
          { id: 'personal', label: 'Personal', icon: User },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={mobileTab === id}
            onClick={() => setMobileTab(id)}
            className={`flex-1 flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
              mobileTab === id
                ? 'bg-white text-primary-700 shadow-sm border border-primary-200'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {mobileTab === 'shared' ? (
        <div role="tabpanel">
          <SharedContextColumn
            sharedTeamView={sharedTeamView}
            recapOutputs={recapOutputs}
            meetingId={meetingId}
            audienceMode={audienceMode}
            pulseBarVisible={pulseBarVisible}
          />
        </div>
      ) : null}
      {mobileTab === 'personal' ? (
        <div role="tabpanel">
          <PersonalContextColumn
            personalView={personalView}
            companionView={companionView}
            memberPrivateGoalsState={memberPrivateGoalsState}
            pulseBarVisible={pulseBarVisible}
            onPersonalAction={onPersonalAction}
            readOnly={readOnly}
          />
        </div>
      ) : null}
    </div>
  )
}

// ── Main Panel ─────────────────────────────────────────────────

export default function MeetingLiveInsightsPanel({
  insights,
  loading,
  error,
  readOnly = false,
  emptyMessage = 'No Live Controls snapshot is available yet.',
  onPersonalAction,
  meetingStartedAtMs = null,
  recapOutputs = [],
  memberPrivateGoalsState = null,
  meetingId = null,
  audienceMode = 'internal',
  lateStart = null,
  onResolveLateStart = null,
  onUndoLateStart = null,
  onRefreshLateStart = null,
  className = '',
}) {
  const [pendingByKey, setPendingByKey] = useState({})

  const runAction = async (key, fn) => {
    if (!fn) return
    setPendingByKey((prev) => ({ ...prev, [key]: true }))
    try {
      await fn()
    } finally {
      setPendingByKey((prev) => ({ ...prev, [key]: false }))
    }
  }

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <ToneFogOverlay intensity={negativityIntensityFromInsights(insights)} />
      {loading && !insights ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading live insights...
        </div>
      ) : null}

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      ) : null}

      {insights?.dataMode === 'persisted_snapshot' ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {insights.sourceMeetingEndedAtMs
            ? `Showing the final saved Live Controls snapshot from ${new Date(insights.sourceMeetingEndedAtMs).toLocaleString()}.`
            : 'Showing the final saved Live Controls snapshot for the last completed meeting.'}
        </div>
      ) : null}

      {insights ? (() => {
        const pulseBarVisible = (insights.sharedTeamView.topicRows ?? []).length > 0
        return (
        <div className="flex flex-col gap-2">
          {/* Zone 1: Meeting Pulse Bar */}
          <MeetingPulseBar
            topicRows={insights.sharedTeamView.topicRows ?? []}
            activeAgendaItemId={insights.sharedTeamView.activeAgendaItemId}
            activeAgendaItemTitle={insights.sharedTeamView.activeAgendaItemTitle}
            topicSummary={insights.sharedTeamView.topicSummary}
            isLive={!readOnly}
            meetingStartedAtMs={meetingStartedAtMs}
          />

          {/* Companion view surfaces (rolling summary, coaching cues, parking
              lot) used to render here as a violet block. They were moved into
              PersonalContextColumn — see "Where We Are", "Coaching cues", and
              "Parked" sections there. Time-critical surfaces (wrapupPhase,
              agendaDrift) were dropped: they're incoherent for the Teams/Meet
              inferred-agenda case (no upfront budget to wrap against, no
              fixed agenda to drift from). Reflective surfaces (talkEquity
              per-participant, pairwiseTension, engagementTempo,
              decisionMomentum, topicTimeBudget, tangentsByParticipant) were
              removed from the live UI — they're noise mid-meeting; data still
              accumulates for post-meeting analytics. */}

          {/* Late Start Band — only visible to organizers (non-readOnly) */}
          {!readOnly && lateStart ? (
            <LateStartBand
              lateStart={lateStart}
              meetingId={meetingId}
              onResolve={onResolveLateStart}
              onUndo={onUndoLateStart}
              onRefresh={onRefreshLateStart}
            />
          ) : null}

          {/* Zone 2: Action Required Band */}
          <ActionRequiredBand
            personalQueue={insights.personalView.personalQueue ?? []}
            topicRows={insights.sharedTeamView.topicRows ?? []}
            attentionSignals={insights.sharedTeamView.attentionSignals ?? []}
            onPersonalAction={onPersonalAction}
            runAction={runAction}
            pendingByKey={pendingByKey}
            readOnly={readOnly}
          />

          {/* Zone 3: Intelligence Feed — two columns on lg+, tabs below lg */}
          <div className="hidden lg:grid lg:items-start lg:gap-4 lg:grid-cols-2">
            <SharedContextColumn
              sharedTeamView={insights.sharedTeamView}
              recapOutputs={recapOutputs}
              meetingId={meetingId}
              audienceMode={audienceMode}
              pulseBarVisible={pulseBarVisible}
            />
            <PersonalContextColumn
              personalView={insights.personalView}
              companionView={insights.sharedTeamView.companionView ?? null}
              memberPrivateGoalsState={memberPrivateGoalsState}
              pulseBarVisible={pulseBarVisible}
              onPersonalAction={onPersonalAction}
              readOnly={readOnly}
            />
          </div>

          {/* Mobile/Tablet: tab switcher */}
          <MobileTabView
            sharedTeamView={insights.sharedTeamView}
            personalView={insights.personalView}
            companionView={insights.sharedTeamView.companionView ?? null}
            recapOutputs={recapOutputs}
            memberPrivateGoalsState={memberPrivateGoalsState}
            meetingId={meetingId}
            audienceMode={audienceMode}
            pulseBarVisible={pulseBarVisible}
            onPersonalAction={onPersonalAction}
            readOnly={readOnly}
          />
        </div>
        )
      })() : !loading && !error ? (
        <div className="glass rounded-2xl p-6 text-sm text-gray-600">{emptyMessage}</div>
      ) : null}
    </div>
  )
}
